import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Decode JWT payload and check if token is expired (client-side, no verification).
 * Returns true if token is expired, invalid, or missing.
 */
/** Roles that may access the client portal (JWT may be legacy `client` or new `client_portal`). */
export const CLIENT_PORTAL_ROLES = ['client', 'client_portal'];

export function isClientPortalRole(role) {
  return CLIENT_PORTAL_ROLES.includes(role);
}

function isTokenExpired(token) {
  if (!token || typeof token !== 'string') return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    if (!payload.exp) return false; // No exp claim = treat as valid
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  } catch {
    return true; // Invalid token = treat as expired
  }
}

/**
 * Authentication store using Zustand
 * Persists auth state to localStorage
 */
export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      isAuthenticated: false,
      
      // Actions
      login: (userData, authToken) => {
        try {
          if (userData?.portalSlug) {
            sessionStorage.setItem('lastPortalSlug', String(userData.portalSlug));
          }
        } catch {
          /* ignore */
        }
        set({
          user: userData,
          token: authToken,
          isAuthenticated: true,
        });
      },
      
      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
        // Clear localStorage
        localStorage.removeItem('auth-storage');
      },

      /** Clear auth if token is expired. Call on app mount. */
      checkTokenExpiry: () => {
        if (isTokenExpired(get().token)) {
          get().logout();
        }
      },
      
      updateUser: (userData) => {
        set(state => ({
          user: { ...state.user, ...userData },
        }));
      },
      
      // Getters
      getToken: () => get().token,
      getUser: () => get().user,
      getUserRole: () => get().user?.role,
      getTenantId: () => get().user?.tenantId,
      isTokenExpired: () => isTokenExpired(get().token),
      
      // Role checks
      isGlobalAdmin: () => get().user?.role === 'global_admin',
      isTenantAdmin: () => get().user?.role === 'tenant_admin',
      isTenantUser: () => get().user?.role === 'tenant_user',
      isClient: () => isClientPortalRole(get().user?.role),
      
      // Permission checks
      canAccessAdmin: () => get().user?.role === 'global_admin',
      canAccessTenant: () => ['tenant_admin', 'tenant_user'].includes(get().user?.role),
      canAccessClientPortal: () => isClientPortalRole(get().user?.role),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

